import type { IconDefinition } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCalendarCheck, faEye, faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
// The redaction icon is the filled person in the design, not the outline one
import { faUser } from "@performyard/fontawesome/pro-solid-svg-icons";
import type {
	BulkUpdateReviewCycleReportsRequestInput,
	ReviewCycleReportRowFrontend,
} from "@performyard/service-contracts";
import { convertToZonedUTCTime } from "@performyard/utils-isomorphic";

const BULK_ACTION_KEYS = {
	authorDueDate: "authorDueDate",
	signerDueDate: "signerDueDate",
	visibility: "visibility",
	redacted: "redacted",
	delete: "delete",
} as const;

export type BulkActionKey = (typeof BULK_ACTION_KEYS)[keyof typeof BULK_ACTION_KEYS];

/** Select actions have no preselected choice, so Save Changes stays disabled until one is picked. */
export type SelectValue = "enabled" | "disabled";

/**
 * Each action carries its own RBAC permission rather than sharing one "can edit" gate, because
 * redaction and deletion are held by fewer roles than the rest: a cycle group admin has
 * `editCycle` and `deleteFormsInCycle` but not `addRedactedFormsInCycle`.
 */
type BulkActionPermission =
	| "review_cycle:editCycle"
	| "review_cycle:deleteFormsInCycle"
	| "review_cycle:addRedactedFormsInCycle";

type BulkUpdateAction = {
	key: Exclude<BulkActionKey, "delete">;
	/** Modal heading */
	label: string;
	/** Shortened name for the banner's action button, which is tight on space */
	buttonLabel: string;
	icon: IconDefinition;
	/** The cycle permission the acting user needs for this action */
	permission: BulkActionPermission;
	control: "date" | "select";
	/** Label shown above the action's control in the modal */
	fieldLabel: string;
	/** Trails the bolded form count in the modal subtitle */
	subtitleSuffix: string;
	/** Only the due-date modals note the company timezone */
	showsTimezoneNote: boolean;
	/** Choices for a select action */
	options?: { label: string; value: SelectValue }[];
};

type BulkDeleteAction = {
	key: "delete";
	label: string;
	buttonLabel: string;
	icon: IconDefinition;
	permission: BulkActionPermission;
	control: "confirmation";
	subtitleSuffix: string;
	fieldLabel?: undefined;
	showsTimezoneNote?: false;
	options?: undefined;
};

export type BulkAction = BulkUpdateAction | BulkDeleteAction;

/** Select choices mirror the labels the table's own chips use, so the wording matches the grid */
const VISIBILITY_OPTIONS: { label: string; value: SelectValue }[] = [
	{ label: "Visible", value: "enabled" },
	{ label: "Not Visible", value: "disabled" },
];

const REDACTED_OPTIONS: { label: string; value: SelectValue }[] = [
	{ label: "Yes", value: "enabled" },
	{ label: "No", value: "disabled" },
];

export const BULK_ACTIONS: BulkAction[] = [
	{
		key: BULK_ACTION_KEYS.authorDueDate,
		label: "Change Author Due Date",
		buttonLabel: "Set Author Due",
		icon: faCalendarCheck,
		permission: "review_cycle:editCycle",
		control: "date",
		fieldLabel: "Author Due Date",
		subtitleSuffix: "Author Due Date to:",
		showsTimezoneNote: true,
	},
	{
		key: BULK_ACTION_KEYS.signerDueDate,
		label: "Change Signer Due Date",
		buttonLabel: "Set Signer Due",
		icon: faCalendarCheck,
		permission: "review_cycle:editCycle",
		control: "date",
		fieldLabel: "Signer Due Date",
		subtitleSuffix: "Signer Due Date to:",
		showsTimezoneNote: true,
	},
	{
		key: BULK_ACTION_KEYS.visibility,
		label: "Set Visibility",
		buttonLabel: "Set Visibility",
		icon: faEye,
		permission: "review_cycle:editCycle",
		control: "select",
		fieldLabel: "Visibility",
		subtitleSuffix: "Visibility Settings",
		showsTimezoneNote: false,
		options: VISIBILITY_OPTIONS,
	},
	{
		key: BULK_ACTION_KEYS.redacted,
		label: "Redact Authors",
		buttonLabel: "Set Redacted",
		icon: faUser,
		// Not editCycle: a cycle group admin can edit the cycle but never change author redaction
		permission: "review_cycle:addRedactedFormsInCycle",
		control: "select",
		fieldLabel: "Redact Authors",
		subtitleSuffix: "Redacted Settings",
		showsTimezoneNote: false,
		options: REDACTED_OPTIONS,
	},
	{
		key: BULK_ACTION_KEYS.delete,
		label: "Delete Forms",
		buttonLabel: "Delete Forms",
		icon: faTrashCan,
		permission: "review_cycle:deleteFormsInCycle",
		control: "confirmation",
		subtitleSuffix: "from this review cycle",
	},
];

/** The actions the acting user holds the permission for, in banner order */
export const getAvailableBulkActions = (permissions: string[]): BulkAction[] =>
	BULK_ACTIONS.filter((action) => permissions.includes(action.permission));

export const getBulkAction = (key: BulkActionKey): BulkAction => {
	const action = BULK_ACTIONS.find((candidate) => candidate.key === key);
	if (!action) throw new Error(`Unknown bulk action: ${key}`);
	return action;
};

type BulkUpdates = BulkUpdateReviewCycleReportsRequestInput["updates"];

/**
 * Builds the PATCH body's `updates` for a value-setting action. The picker holds a company-timezone
 * wall clock, so dates convert back to a UTC instant, as in the Edit Cycle Details modal.
 */
export const buildBulkUpdates = ({
	action,
	dateValue,
	selectValue,
	companyTimezone,
}: {
	action: BulkAction;
	dateValue?: Date | null;
	selectValue?: SelectValue;
	companyTimezone: string;
}): BulkUpdates => {
	switch (action.key) {
		case BULK_ACTION_KEYS.authorDueDate:
			if (!dateValue) throw new Error("An author due date is required");
			return { authorsDueDate: convertToZonedUTCTime(dateValue, companyTimezone) };
		case BULK_ACTION_KEYS.signerDueDate:
			if (!dateValue) throw new Error("A signer due date is required");
			return { signersDueDate: convertToZonedUTCTime(dateValue, companyTimezone) };
		case BULK_ACTION_KEYS.visibility:
			if (!selectValue) throw new Error("A visibility value is required");
			return { visible: selectValue === "enabled" };
		case BULK_ACTION_KEYS.redacted:
			if (!selectValue) throw new Error("A redaction value is required");
			return { redacted: selectValue === "enabled" };
		default:
			throw new Error(`${action.key} is not a value-setting bulk action`);
	}
};

/** The two due dates bound each other, so each is validated against the other's stored value */
export type DueDateBounds = Pick<ReviewCycleReportRowFrontend, "authorDueDate" | "signerDueDate">;

/** The stream can hand these back as ISO strings, so normalize before comparing instants */
const toInstant = (value: DueDateBounds["authorDueDate"]): number | null =>
	value === null || value === undefined ? null : new Date(value).getTime();

/**
 * Counts the selected forms the new due date would invalidate: an author due date has to land on
 * or before its form's signer due date, and a signer due date on or after its author due date.
 *
 * Forms with no counterpart date are skipped rather than counted — a form with no signers has no
 * signer due date, so there is nothing for the new author due date to run past.
 */
export const countDueDateConflicts = ({
	action,
	dateValue,
	rows,
	companyTimezone,
}: {
	action: BulkAction;
	dateValue: Date;
	rows: DueDateBounds[];
	companyTimezone: string;
}): number => {
	// The picker holds a company-timezone wall clock, so compare the instant that would be saved
	const target = convertToZonedUTCTime(dateValue, companyTimezone).getTime();
	if (Number.isNaN(target)) return 0;

	return rows.filter((row) => {
		if (action.key === BULK_ACTION_KEYS.authorDueDate) {
			const signerDue = toInstant(row.signerDueDate);
			return signerDue !== null && target > signerDue;
		}

		const authorDue = toInstant(row.authorDueDate);
		return authorDue !== null && target < authorDue;
	}).length;
};

/** Rule text plus the phrase naming the counterpart date, per due-date action */
const DUE_DATE_CONFLICT_COPY = {
	[BULK_ACTION_KEYS.authorDueDate]: {
		rule: "Author Due Date must be on or before the Signer Due Date",
		counterpart: "an earlier Signer Due Date",
	},
	[BULK_ACTION_KEYS.signerDueDate]: {
		rule: "Signer Due Date must be on or after the Author Due Date",
		counterpart: "a later Author Due Date",
	},
} as const;

/**
 * When every selected form conflicts, the rule alone says it. When only some do, the message names
 * how many, so the admin knows the rest of the selection would have applied cleanly.
 */
export const buildDueDateConflictMessage = ({
	action,
	conflictingCount,
	selectedCount,
}: {
	action: BulkAction;
	conflictingCount: number;
	selectedCount: number;
}): string => {
	const copy =
		action.key === BULK_ACTION_KEYS.authorDueDate
			? DUE_DATE_CONFLICT_COPY[BULK_ACTION_KEYS.authorDueDate]
			: DUE_DATE_CONFLICT_COPY[BULK_ACTION_KEYS.signerDueDate];

	if (conflictingCount >= selectedCount) return `${copy.rule}.`;

	return `${copy.rule} for all selected forms. ${conflictingCount} of ${formCountLabel(selectedCount)} have ${copy.counterpart}.`;
};

/** "This action will change **10 forms** Visibility Settings" — the count is bolded by the modal */
export const buildSubtitleParts = (
	action: BulkAction,
	count: number
): { prefix: string; count: string; suffix: string } => ({
	prefix: action.control === "confirmation" ? "This action will delete " : "This action will change ",
	count: formCountLabel(count),
	suffix: ` ${action.subtitleSuffix}`,
});

/** "1 form" / "12 forms"; `titleCase` gives the banner's "1 Form" / "12 Forms". */
export const formCountLabel = (count: number, { titleCase = false }: { titleCase?: boolean } = {}): string =>
	`${count} ${titleCase ? "Form" : "form"}${count === 1 ? "" : "s"}`;
